import { Component, OnInit, inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { AuthService } from '@core/services/auth.service';
import { CompanyInviteService } from '@core/services/company-invite.service';

@Component({
  selector: 'app-redeem',
  templateUrl: './redeem.component.html',
  styleUrl: './redeem.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
/**
 * RedeemComponent
 *
 * Handles incoming invite links: /register/redeem?inviteCode=XXXXXXXX
 *
 * Flow:
 * - If user is already authenticated → calls the redeemInvite API directly and
 *   navigates to the dashboard on success.
 * - If user is not authenticated → redirects to /login with a returnUrl so the
 *   user is brought back here after signing in.  New users can register from the
 *   login page and the inviteCode is carried along as a query param.
 */
export class RedeemComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private inviteService = inject(CompanyInviteService);
  private authService = inject(AuthService);
  private auth = inject(Auth);
  private platformId = inject(PLATFORM_ID);

  redemptionMessage: string = '';
  isLoading: boolean = false;
  inviteCode!: string;

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const inviteCode = params['inviteCode'];
      this.inviteCode = inviteCode;

      if (!inviteCode) {
        this.redemptionMessage = 'No invite code found in the URL.';
        return;
      }

      this.handleInviteCode(inviteCode);
    });
  }

  /**
   * Main entry point: validate code then branch on auth state.
   */
  private async handleInviteCode(inviteCode: string): Promise<void> {
    this.isLoading = true;
    this.redemptionMessage = '';

    // Validate the invite first (checks existence and expiration via Firestore)
    let isValid: boolean;
    try {
      isValid = await this.inviteService.validateInviteCode(inviteCode);
    } catch (err) {
      this.isLoading = false;
      this.redemptionMessage = 'Unable to validate invite code. Please try again.';
      console.error('Error validating invite code:', err);
      return;
    }

    if (!isValid) {
      this.isLoading = false;
      this.redemptionMessage = 'This invite link is invalid or has expired.';
      return;
    }

    const currentUser = this.auth.currentUser;

    if (currentUser) {
      // User is already signed in – redeem via API immediately
      await this.redeemForAuthenticatedUser(inviteCode);
    } else {
      // Not signed in: redirect to login; after sign-in the user will be
      // redirected back to this page which will then redeem automatically.
      this.redirectToLogin(inviteCode);
    }
  }

  /**
   * Call the backend redeemInvite endpoint and navigate to the dashboard.
   */
  private async redeemForAuthenticatedUser(inviteCode: string): Promise<void> {
    try {
      const result = await this.inviteService.redeemInviteViaApi(inviteCode);
      if (result.success) {
        // Redemption assigned a company on the server, so this session's user
        // document has changed underneath us. Republish it before routing: the
        // wait page answers from AuthService's in-memory copy, which still
        // predates the redemption (#524).
        await this.authService.refreshHydratedUser();
        // Navigate to logging-in which re-validates session and routes to dashboard
        await this.router.navigate(['/logging-in']);
      } else {
        this.isLoading = false;
        this.redemptionMessage = result.error || 'Failed to redeem invitation.';
      }
    } catch (error: any) {
      this.isLoading = false;
      this.redemptionMessage =
        error?.error?.error || error?.message || 'Failed to redeem invitation.';
    }
  }

  /**
   * Store the invite code and redirect to login so the user can authenticate.
   * The returnUrl brings them back here after login.
   */
  private redirectToLogin(inviteCode: string): void {
    if (isPlatformBrowser(this.platformId)) {
      // Persist the invite code so it survives the login redirect
      sessionStorage.setItem('pendingInviteCode', inviteCode);
    }
    const returnUrl = `/register/redeem?inviteCode=${encodeURIComponent(inviteCode)}`;
    this.router.navigate(['/login'], { queryParams: { returnUrl } });
  }

  /** Navigate to the signup page, carrying the invite code */
  goToSignUp(): void {
    this.router.navigate(['/register'], {
      queryParams: { inviteCode: this.inviteCode },
    });
  }
}
